<template>
  <div>
    <Subheader title="Build Your Project" />

    <div class="app-content-column">
      <div class="card">
        <form class="form" @submit.stop.prevent="submitProject">
          <h5>Create your project and start funding today!</h5>
          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="title">
            <input
              id="title"
              v-model="project.title"
              type="text"
              placeholder="Project Name"
              required
            />
          </Field>

          <h5>PROJECT DETAILS</h5>
          <p>
            Start with a brief description of the problem and your proposed solution. Then describe
            the impact that your project will have for you and your community. Include specific
            details, # of people, timelines, etc. Every project requires a location. If you cannot
            share your location, please contact your provider for assistance!
          </p>
          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="headline">
            <label for="headline" class="label">Headline</label>
            <textarea
              id="headline"
              v-model="project.headline"
              placeholder="Headline that captures the project's potential impact"
              class="textarea"
              required
            />
          </Field>

          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="shortDescription">
            <label for="shortDescription" class="label">Brief Description</label>
            <textarea
              id="shortDescription"
              v-model="project.shortDescription"
              placeholder="Brief Description of the Problem and your Solution (limit 300 characters)"
              class="textarea"
              rows="6"
              maxlength="300"
              required
            />
          </Field>

          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="impact">
            <label for="impact" class="label">Desired Impact</label>
            <textarea
              id="impact"
              v-model="project.impact"
              placeholder="Description of the impact that your project will have for you and your community (limit 300 characters)"
              class="textarea"
              rows="6"
              maxlength="300"
              required
            />
          </Field>

          <h5>PROJECT ADDRESS</h5>
          <p>Please provide an address of where your project is located.</p>
          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="location">
            <input v-model="project.location" placeholder="Address" required />
          </Field>

          <!-- <h5>LOCATION OF MAKER</h5>
          <p>This is an optional step, but sharing your location speeds up the verification process.</p>
          <div v-if="project.longitude && project.latitude">
            <MapWithCircle :map-center="mapCenter" :circle-center="mapCenter" />
          </div>
          <div class="btn-container">
            <button
              v-if="geolocationSupported && (!project.longitude || !project.latitude)"
              class="medium-button medium-button--inverted"
              @click="fetchCoords"
            >{{ locationLoading ? 'Fetching...' : 'SEND MY LOCATION' }}</button>
            <button
              v-if="geolocationSupported && project.longitude && project.latitude"
              class="medium-button"
              @click="fetchCoords"
            >RESEND MY LOCATION</button>
          </div> -->

          <h5>ATTRIBUTES</h5>
          <p>
            Select the attributes that are most relevant to your project to help people discover
            what you're doing!
          </p>
          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="attributes">
            <h6>Project Location</h6>
            <div class="button-wrapper">
              <button
                v-for="attr in locationAttrs"
                :key="attr.id"
                type="button"
                class="tag"
                :class="{ 'tag--inverted': attr.selected }"
                @click="toggleAttributes(attr)"
              >
                {{ attr.name }}
              </button>
            </div>

            <h6>Project Interests</h6>
            <div class="button-wrapper">
              <button
                v-for="attr in interestAttrs"
                :key="attr.id"
                type="button"
                class="tag"
                :class="{ 'tag--inverted': attr.selected }"
                @click="toggleAttributes(attr)"
              >
                {{ attr.name }}
              </button>
            </div>
          </Field>

          <h5>PROJECT PHOTO</h5>
          <p>Show off your best photo from your project.</p>
          <input ref="resultFiles" type="file" @change="previewFile" />

          <div class="button-wrapper is-centered">
            <button
              type="submit"
              class="large-button"
              style="margin-top: 3rem;"
              :disabled="loading"
              :class="{ 'get-started-btn--disabled': loading }"
            >
              Set Your Goals
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import Subheader from '@/components/Subheader'
import Field from '@/components/Field'
// import MapWithCircle from '@/components/MapWithCircle'

import Attribute from '@/services/attributes'
import Project from '@/services/projects'
import { currentCoordinates } from '@/services/location/location'
import { validateInputLength, validateSelectMinimum } from '@/services/forms'

export default {
  name: 'NewProject',
  components: {
    Subheader,
    Field,
    // MapWithCircle,
  },
  data() {
    return {
      project: Project.create(),
      newMaker: false,
      photo: null,
      locationAttrs: [],
      interestAttrs: [],
      fieldErrors: [],
      showFieldErrors: false,
      locationLoading: false,
      geolocationSupported: 'geolocation' in navigator,
      mapCenter: { lat: null, lng: null },
      circleCenter: { lat: null, lng: null },
      loading: false,
    }
  },
  computed: {
    ...mapGetters(['getUser']),
  },
  mounted() {
    // Keep track of whether user is a recently-created Maker,
    // so we can navigate them appropriately
    if (this.$route.params.newMaker) {
      this.newMaker = true
    }
    this.listAttrs()
  },
  methods: {
    previewFile(event) {
      this.photo = event.target.files[0]
    },
    /* Verify user has filled out all required fields */
    fieldHasError(fieldName) {
      return () => this.errors.filter(error => error.field === fieldName).length > 0
    },
    getFieldErrorDetail(fieldName) {
      return () => {
        const error = this.errors.filter(e => e.field === fieldName)[0]
        if (error) {
          return error.detail
        }
        return ''
      }
    },
    /* Post project and navigate to next page */
    async submitProject() {
      this.loading = true
      this.fieldErrors = this.collectFieldErrors()

      if (this.fieldErrors.length) {
        this.showFieldErrors = true
        let element = document.getElementById('title')
        element.scrollIntoView({ behavior: 'smooth' })
      } else {
        try {
          const projectResponse = await Project.api.create({
            title: this.project.title,
            shortDescription: this.project.shortDescription,
            headline: this.project.headline,
            impact: this.project.impact,
            location: this.project.location,
            attributes: this.project.attributes,
            latitude: this.project.latitude,
            longitude: this.project.longitude,
            maker: this.getUser.id,
          })
          const newProjectId = projectResponse.id

          if (this.photo) {
            await Project.api.uploadProfilePic(newProjectId, this.photo)
            this.$router.push({
              name: 'NewProjectGoals',
              params: { projectId: newProjectId, newMaker: this.newMaker },
            })
          } else {
            this.$router.push({
              name: 'NewProjectGoals',
              params: { projectId: newProjectId, newMaker: this.newMaker },
            })
          }
        } catch (error) {
          const { response } = error
          if (response && response.data) {
            this.formErrors = response.data
            this.showFormErrors = true
          }
          this.loading = false
          return
        }
      }
      this.loading = false
    },
    collectFieldErrors() {
      //client-side validation
      //required: title, headline, shortDescription, impact, location, attributes
      const requiredFields = [
        'title',
        'headline',
        'shortDescription',
        'impact',
        'location',
        'attributes',
      ]
      const errors = []

      requiredFields.forEach(field => {
        if (field === 'attributes') {
          const error = validateSelectMinimum(field, this.project[field])
          if (error) {
            errors.push(error)
          }
        } else {
          const error = validateInputLength(field, this.project[field])
          if (error) {
            errors.push(error)
          }
        }
      })

      return errors
    },
    /* GET Attributes list */
    async listAttrs() {
      this.loading = true
      let attrList = await Attribute.api.list()
      attrList = attrList.results.map(attr => ({ ...attr, selected: false }))
      this.locationAttrs = attrList.filter(attr => attr.group === 'Location')
      this.interestAttrs = attrList.filter(attr => attr.group === 'Interest Area')
      this.loading = false
    },
    /* Toggle attribute selection */
    toggleAttributes(attr) {
      if (attr.selected) {
        const remainingAttrs = this.project.attributes.filter(e => {
          return e !== attr.id
        })
        this.project.attributes = remainingAttrs
        attr.selected = false
      } else if (!attr.selected) {
        this.project.attributes.push(attr.id)
        attr.selected = true
      }
    },
    /* GET user's current coordinates */
    async fetchCoords() {
      try {
        this.project.latitude = null
        this.project.longitude = null
        this.locationLoading = true
        let coords = await currentCoordinates()
        this.project.latitude = parseFloat(coords.latitude).toFixed(6)
        this.project.longitude = parseFloat(coords.longitude).toFixed(6)
        this.mapCenter.lat = parseFloat(coords.latitude)
        this.mapCenter.lng = parseFloat(coords.longitude)
      } finally {
        this.locationLoading = false
      }
    },
    needHelpAlert() {
      this.$swal({
        title: 'Need help on your first project upload? Start here',
        text:
          '1) View a few of our favorite projects to see what works well at Mzingo Partners! \n\n2) Email mzingopartners@gmail.com to have our team brainstorm with you.\n\n3) Request an introduction to your local activator for assistance.',
        icon: 'info',
        button: {
          text: 'Got it',
          value: false,
          visible: true,
          className: 'get-started-btn',
          closeModal: true,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/mixins/cards';
@import '../styles/mixins/buttons';
@import '../styles/forms';
@import '../styles/layout';

.card {
  @include card-w-borders();
}

.get-started-btn {
  @include large-button();

  &--disabled {
    background-color: $soft-gray;
    color: $base-gray;
  }
}

// .btn-container {
//   display: flex;
//   justify-content: center;
// }
</style>
